import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Lifecycle Marketing Systems for Customer Journey Optimization",
  seoDescription: "Lifecycle marketing that sequences every touchpoint. Build automated systems that move customers through stages, not just send campaigns.",
  canonicalUrl: "https://xperfomax.com/lifecycle-marketing",
  category: "services",
  categoryLabel: "Services / Lifecycle Marketing",
  headline: "Lifecycle Marketing Systems for Customer Journey Optimization",
  subheadline: "Lifecycle marketing that sequences every touchpoint. Build automated systems that move customers through stages, not just send campaigns.",
  ctaText: "Start a growth conversation",
  problemTitle: "Why Lifecycle Programs Underperform",
  problems: [
    "Campaigns fire on schedules, not customer behavior.",
    "Customer stages are undefined or inconsistent.",
    "Touchpoints are siloed across channels without coordination.",
    "Revenue is attributed to campaigns, not customer progression.",
  ],
  systemTitle: "Our Lifecycle System",
  systemDescription: "Stage-based architecture, behavioral triggers, and cross-channel orchestration built for measurable progression.",
  services: [
    { name: "Journey Mapping", icon: "GitBranch", components: ["Customer stage identification", "Touchpoint optimization", "Gap analysis and sequencing"], outcome: "Clear visibility into every stage of the customer relationship." },
    { name: "Trigger-Based Messaging", icon: "Clock", components: ["Behavioral trigger design", "Real-time event tracking", "Conditional logic flows"], outcome: "Right message delivered at the exact right moment." },
    { name: "Onboarding Sequences", icon: "Users", components: ["Welcome flow optimization", "Activation milestones", "Value demonstration cadence"], outcome: "New customers reach value faster and stay longer." },
    { name: "Retention Automation", icon: "RefreshCw", components: ["Churn prediction triggers", "Win-back campaigns", "Re-engagement sequences"], outcome: "Prevent churn before it happens with proactive outreach." },
    { name: "Cross-Channel Orchestration", icon: "Layers", components: ["Email, SMS, push coordination", "Channel preference optimization", "Unified messaging strategy"], outcome: "Consistent experience across every touchpoint." },
    { name: "Lifecycle Analytics", icon: "BarChart3", components: ["Stage conversion tracking", "Cohort analysis", "Revenue attribution by stage"], outcome: "Understand which lifecycle moments drive the most value." },
  ],
  processTitle: "From audit to orchestration.",
  processSteps: [
    { number: "01", title: "Lifecycle Audit", description: "Map current customer journey, identify stage gaps, and quantify revenue leakage at each transition." },
    { number: "02", title: "Stage Definition", description: "Define clear lifecycle stages with measurable entry and exit criteria tied to business outcomes." },
    { number: "03", title: "Trigger Architecture", description: "Design behavioral triggers and conditional logic that respond to customer actions in real-time." },
    { number: "04", title: "Sequence Development", description: "Build messaging sequences for each stage transition with personalization and A/B testing frameworks." },
    { number: "05", title: "Orchestration & Scale", description: "Deploy cross-channel automation with ongoing optimization based on lifecycle revenue attribution." },
  ],
  timelinePhases: [
    { number: "01", title: "Lifecycle Audit", description: "Map current customer journey and identify gaps.", decision: "Which stages leak the most revenue.", output: "Journey map with stage-by-stage conversion analysis.", riskRemoved: "Hidden friction points in customer progression." },
    { number: "02", title: "Stage Definition", description: "Define measurable lifecycle stages.", decision: "What behaviors signal stage transitions.", output: "Stage framework with entry and exit criteria.", riskRemoved: "Ambiguous customer segmentation." },
    { number: "03", title: "Trigger Architecture", description: "Design behavioral triggers and conditional logic.", decision: "Which actions warrant automated responses.", output: "Trigger logic documentation with flow diagrams.", riskRemoved: "Missed moments of customer intent." },
    { number: "04", title: "Sequence Development", description: "Build messaging for each stage transition.", decision: "What content converts at each stage.", output: "Deployed sequences with A/B frameworks.", riskRemoved: "Generic messaging that ignores context." },
    { number: "05", title: "Orchestration & Scale", description: "Deploy cross-channel automation.", decision: "Which channels work best for which stages.", output: "Unified orchestration with revenue dashboards.", riskRemoved: "Fragmented customer experience." },
  ],
  technologyCategories: [
    { title: "Marketing Automation", icon: "Send", items: ["HubSpot", "Klaviyo", "Braze", "Iterable"] },
    { title: "Customer Data", icon: "Database", items: ["Segment", "mParticle", "Amplitude"] },
    { title: "Analytics", icon: "LineChart", items: ["Mixpanel", "GA4", "Looker Studio"] },
    { title: "Orchestration", icon: "Target", items: ["Customer.io", "Ortto", "ActiveCampaign"] },
  ],
  techTitle: "Lifecycle Marketing Tools",
  techDescription: "Enterprise-grade automation and customer data platforms.",
  qualification: {
    forYou: [
      "You have an existing customer base with repeat purchase potential",
      "You can track customer behavior across touchpoints",
      "You want to increase LTV without increasing acquisition spend",
      "You are ready to invest in automation infrastructure",
    ],
    notForYou: [
      "You are focused purely on new customer acquisition",
      "You have no customer data or tracking in place",
      "You want quick wins without building systems",
      "You have a one-time purchase business model",
    ],
  },
  faqs: [
    { question: "What is lifecycle marketing?", answer: "Lifecycle marketing is a strategy that delivers targeted messaging based on where customers are in their relationship with your brand. Instead of one-size-fits-all campaigns, it sequences communication to match customer stage and intent." },
    { question: "How is lifecycle marketing different from email marketing?", answer: "Email marketing is a channel. Lifecycle marketing is a strategy that spans all channels. It determines what to say, when to say it, and through which channel based on customer behavior and stage." },
    { question: "How long does it take to implement lifecycle marketing?", answer: "A foundational lifecycle program can be deployed in 6 to 8 weeks. Full optimization with cross-channel orchestration typically takes 3 to 4 months to mature as you gather performance data." },
    { question: "What data do we need to get started?", answer: "At minimum, you need customer transaction history and email engagement data. Behavioral data like product usage, page visits, and app events significantly improve targeting and timing." },
  ],
  finalCtaTitle: "Build a system that compounds customer value.",
  finalCtaDescription: "Most brands run campaigns. We build lifecycle systems that move customers forward at every stage.",
  finalCtaButtonText: "Start a growth conversation",
};

const LifecycleMarketing = () => {
  const { content } = usePageContent("lifecycle-marketing", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      timelinePhases={content.timelinePhases}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default LifecycleMarketing;
